import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Calculator,
  Trash2, MapPin, ExternalLink, Package, X,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  HIGH_BUILD_EPOXY,
  FLAKE_PRODUCTS,
  POLYASPARTIC_SEAL,
  HIGH_BUILD_SYSTEMS,
  FLAKE_COVERAGE,
  HIGH_BUILD_COLOUR_CHART,
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import { toast } from 'sonner';
import { buildManualItems, itemsTotal } from '@/components/proepoxy/highbuild-flow/guidedItems';
import PrioritySelector from '@/components/PrioritySelector';

export default function HighBuildManualFlow({ state, priority, setPriority, currentStep, goToStep, highBuildOrderMode, setHighBuildOrderMode, navigate, onExit , voucher: voucherProp }) {
  const {
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty,
  } = state;
  const voucher = voucherProp || { id: null, value: 0 };
  const voucherAreaM2 = (state.highBuildAreas || []).reduce((a, x) => a + (parseFloat(x.m2) || 0), 0);
  const voucherOff = Math.min(voucher.value || 0, calculateManualOrderTotal());
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Manual Order</h2>
                    <Button onClick={() => setShowColourChart(true)} variant="outline" className="border-2 border-[#7A0B7A] text-[#7A0B7A]">
                      <ExternalLink className="w-4 h-4 mr-2" />View Colour Chart
                    </Button>
                  </div>
                  <p className="text-gray-600 mb-6">Select the products and quantities you need.</p>

                  {/* Colour Chart Modal */}
                  {showColourChart && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6 relative">
                        <button onClick={() => setShowColourChart(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
                          <span className="text-2xl">×</span>
                        </button>
                        <h3 className="text-xl font-bold text-[#27292E] mb-4">Colour Chart</h3>
                        <img src={HIGH_BUILD_COLOUR_CHART} alt="High Build Colour Chart" className="w-full rounded-xl" />
                      </div>
                    </div>
                  )}

                  {/* Floor Preparation Section */}
                  <div className="mb-8">
                    <h3 className="font-bold text-[#27292E] mb-4 pb-2 border-b">Floor Preparation</h3>
                    
                    {/* Self Smoothing Compounds */}
                    <div className="mb-6">
                      <h4 className="font-medium text-[#27292E] mb-3">Do you need self smoothing compounds?</h4>
                      <div className="flex gap-3 mb-3">
                        <button
                          onClick={() => setManualOrder(prev => ({ ...prev, needsSmoothingCompounds: true }))}
                          className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                            manualOrder.needsSmoothingCompounds === true
                              ? 'bg-[#7A0B7A] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setManualOrder(prev => ({ ...prev, needsSmoothingCompounds: false, smoothingCompounds: {} }))}
                          className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                            manualOrder.needsSmoothingCompounds === false
                              ? 'bg-[#7A0B7A] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          No
                        </button>
                      </div>

                      {manualOrder.needsSmoothingCompounds && (
                        <div className="space-y-3">
                          {FLOOR_PREP_PRODUCTS.smoothing_compounds.map(product => {
                            const qty = manualOrder.smoothingCompounds?.[product.id] || 0;
                            return (
                              <div key={product.id} className={`p-3 rounded-xl border-2 transition-all ${qty > 0 ? 'border-[#7A0B7A] bg-[#7A0B7A]/5' : 'border-gray-200'}`}>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-[#27292E] text-sm">{product.name}</h4>
                                    <p className="text-xs text-gray-500">{product.type}</p>
                                  </div>
                                  <div className="flex items-center justify-between sm:justify-end gap-3">
                                    <div className="text-left sm:text-right">
                                      <p className="font-bold text-[#7A0B7A] text-sm">£{product.price.toFixed(2)}</p>
                                      <p className="text-xs text-gray-400">per {product.unit}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          const newQty = Math.max(0, qty - 1);
                                          setManualOrder(prev => ({ ...prev, smoothingCompounds: { ...prev.smoothingCompounds, [product.id]: newQty } }));
                                        }}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-8 text-center font-bold">{qty}</span>
                                      <button
                                        onClick={() => {
                                          setManualOrder(prev => ({ ...prev, smoothingCompounds: { ...prev.smoothingCompounds, [product.id]: qty + 1 } }));
                                        }}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                {qty > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                                    <span className="text-gray-600">{qty} × £{product.price.toFixed(2)}</span>
                                    <span className="font-bold text-[#7A0B7A]">£{(qty * product.price).toFixed(2)}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Sub Floor Primers */}
                    <div>
                      <h4 className="font-medium text-[#27292E] mb-3">Do you need sub floor primers?</h4>
                      <div className="flex gap-3 mb-3">
                        <button
                          onClick={() => setManualOrder(prev => ({ ...prev, needsSubFloorPrimer: true }))}
                          className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                            manualOrder.needsSubFloorPrimer === true
                              ? 'bg-[#7A0B7A] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setManualOrder(prev => ({ ...prev, needsSubFloorPrimer: false, subFloorPrimers: {} }))}
                          className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                            manualOrder.needsSubFloorPrimer === false
                              ? 'bg-[#7A0B7A] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          No
                        </button>
                      </div>

                      {manualOrder.needsSubFloorPrimer && (
                        <div className="space-y-3">
                          {FLOOR_PREP_PRODUCTS.primers.map(product => {
                            const qty = manualOrder.subFloorPrimers?.[product.id] || 0;
                            return (
                              <div key={product.id} className={`p-3 rounded-xl border-2 transition-all ${qty > 0 ? 'border-[#7A0B7A] bg-[#7A0B7A]/5' : 'border-gray-200'}`}>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-[#27292E] text-sm">{product.name}</h4>
                                    <p className="text-xs text-gray-500">{product.coverage || product.size}</p>
                                  </div>
                                  <div className="flex items-center justify-between sm:justify-end gap-3">
                                    <div className="text-left sm:text-right">
                                      <p className="font-bold text-[#7A0B7A] text-sm">£{product.price.toFixed(2)}</p>
                                      <p className="text-xs text-gray-400">per {product.unit}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          const newQty = Math.max(0, qty - 1);
                                          setManualOrder(prev => ({ ...prev, subFloorPrimers: { ...prev.subFloorPrimers, [product.id]: newQty } }));
                                        }}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-8 text-center font-bold">{qty}</span>
                                      <button
                                        onClick={() => {
                                          setManualOrder(prev => ({ ...prev, subFloorPrimers: { ...prev.subFloorPrimers, [product.id]: qty + 1 } }));
                                        }}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                {qty > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                                    <span className="text-gray-600">{qty} × £{product.price.toFixed(2)}</span>
                                    <span className="font-bold text-[#7A0B7A]">£{(qty * product.price).toFixed(2)}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Epoxy Primer Section - NEW */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4 pb-2 border-b">
                      <h3 className="font-bold text-[#27292E]">Epoxy Primer</h3>
                      <button
                        onClick={addPrimerItem}
                        className="flex items-center gap-2 px-4 py-2 bg-[#7A0B7A] text-white rounded-lg hover:bg-[#5a085a] transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Add Primer
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">2-Part DPM Epoxy Primer • Available in White, Grey, Black</p>
                    
                    {(manualOrder.primerItems || []).length === 0 ? (
                      <div className="p-8 bg-gray-50 rounded-xl text-center">
                        <p className="text-gray-500">No primers added yet. Click &quot;Add Primer&quot; to add your first primer.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {(manualOrder.primerItems || []).map((item, index) => (
                          <div key={item.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-medium text-[#27292E]">Primer {index + 1}</h4>
                              <button
                                onClick={() => removePrimerItem(item.id)}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            
                            {/* Colour Selection */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Colour</label>
                              <select
                                value={item.color}
                                onChange={(e) => updatePrimerItem(item.id, 'color', e.target.value)}
                                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[#7A0B7A] focus:ring-0"
                              >
                                {EPOXY_PRIMERS['2_part'].colors.map(color => (
                                  <option key={color} value={color}>{color}</option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Pack Sizes */}
                            <div className="grid grid-cols-3 gap-3">
                              {/* 5kg */}
                              <div className="p-3 bg-white rounded-lg">
                                <p className="text-xs text-gray-500 mb-2">5m² • £{EPOXY_PRIMERS['2_part'].packs[0].price.toFixed(2)}</p>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => updatePrimerItem(item.id, 'units5kg', (item.units5kg || 0) - 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#7A0B7A] text-[#7A0B7A]"
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <span className="w-8 text-center font-bold text-sm">{item.units5kg || 0}</span>
                                  <button
                                    onClick={() => updatePrimerItem(item.id, 'units5kg', (item.units5kg || 0) + 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              {/* 20kg */}
                              <div className="p-3 bg-white rounded-lg">
                                <p className="text-xs text-gray-500 mb-2">20m² • £{EPOXY_PRIMERS['2_part'].packs[1].price.toFixed(2)}</p>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => updatePrimerItem(item.id, 'units20kg', (item.units20kg || 0) - 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#7A0B7A] text-[#7A0B7A]"
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <span className="w-8 text-center font-bold text-sm">{item.units20kg || 0}</span>
                                  <button
                                    onClick={() => updatePrimerItem(item.id, 'units20kg', (item.units20kg || 0) + 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              {/* 40kg */}
                              <div className="p-3 bg-white rounded-lg">
                                <p className="text-xs text-gray-500 mb-2">40m² • £{EPOXY_PRIMERS['2_part'].packs[2].price.toFixed(2)}</p>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => updatePrimerItem(item.id, 'units40kg', (item.units40kg || 0) - 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#7A0B7A] text-[#7A0B7A]"
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <span className="w-8 text-center font-bold text-sm">{item.units40kg || 0}</span>
                                  <button
                                    onClick={() => updatePrimerItem(item.id, 'units40kg', (item.units40kg || 0) + 1)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Item subtotal */}
                            {((item.units5kg || 0) + (item.units20kg || 0) + (item.units40kg || 0)) > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                                <span className="text-gray-600">{item.color} Primer</span>
                                <span className="font-bold text-[#7A0B7A]">
                                  £{(
                                    (item.units5kg || 0) * EPOXY_PRIMERS['2_part'].packs[0].price +
                                    (item.units20kg || 0) * EPOXY_PRIMERS['2_part'].packs[1].price +
                                    (item.units40kg || 0) * EPOXY_PRIMERS['2_part'].packs[2].price
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* High Build Epoxy Section - UPDATED */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4 pb-2 border-b">
                      <h3 className="font-bold text-[#27292E]">High Build Epoxy Paint</h3>
                      <button
                        onClick={addHighBuildItem}
                        className="flex items-center gap-2 px-4 py-2 bg-[#7A0B7A] text-white rounded-lg hover:bg-[#5a085a] transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Add Epoxy
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Add multiple colours for different areas/jobs</p>
                    
                    {(manualOrder.highBuildItems || []).length === 0 ? (
                      <div className="p-8 bg-gray-50 rounded-xl text-center">
                        <p className="text-gray-500">No epoxy added yet. Click &quot;Add Epoxy&quot; to add your first colour.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {(manualOrder.highBuildItems || []).map((item, index) => {
                          const selectedColor = HIGH_BUILD_EPOXY.colors.find(c => c.code === item.color);
                          return (
                            <div key={item.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-100">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="font-medium text-[#27292E]">Paint {index + 1}</h4>
                                <button
                                  onClick={() => removeHighBuildItem(item.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                              
                              {/* Colour Selection */}
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Colour</label>
                                <select
                                  value={item.color}
                                  onChange={(e) => updateHighBuildItem(item.id, 'color', e.target.value)}
                                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-[#7A0B7A] focus:ring-0"
                                >
                                  {HIGH_BUILD_EPOXY.colors.map(color => (
                                    <option key={color.code} value={color.code}>{color.code} - {color.name}</option>
                                  ))}
                                </select>
                              </div>
                              
                              {/* Pack Sizes */}
                              <div className="grid grid-cols-2 gap-3">
                                {/* 5kg */}
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-gray-500 mb-2">5kg • 15-20m² • £{HIGH_BUILD_EPOXY.packs[0].price.toFixed(2)}</p>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => updateHighBuildItem(item.id, 'units5kg', (item.units5kg || 0) - 1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#7A0B7A] text-[#7A0B7A]"
                                    >
                                      <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="w-8 text-center font-bold text-sm">{item.units5kg || 0}</span>
                                    <button
                                      onClick={() => updateHighBuildItem(item.id, 'units5kg', (item.units5kg || 0) + 1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                {/* 25kg */}
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-gray-500 mb-2">25kg • 75-100m² • £{HIGH_BUILD_EPOXY.packs[1].price.toFixed(2)}</p>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => updateHighBuildItem(item.id, 'units25kg', (item.units25kg || 0) - 1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#7A0B7A] text-[#7A0B7A]"
                                    >
                                      <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="w-8 text-center font-bold text-sm">{item.units25kg || 0}</span>
                                    <button
                                      onClick={() => updateHighBuildItem(item.id, 'units25kg', (item.units25kg || 0) + 1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Item subtotal */}
                              {((item.units5kg || 0) + (item.units25kg || 0)) > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                                  <span className="text-gray-600">{selectedColor?.name || item.color}</span>
                                  <span className="font-bold text-[#7A0B7A]">
                                    £{(
                                      (item.units5kg || 0) * HIGH_BUILD_EPOXY.packs[0].price +
                                      (item.units25kg || 0) * HIGH_BUILD_EPOXY.packs[1].price
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Flakes Section - Multi-item support */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4 pb-2 border-b">
                      <h3 className="font-bold text-[#27292E]">Flakes</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => addFlakeItem('1kg')}
                          className="flex items-center gap-1 px-3 py-2 bg-[#7A0B7A] text-white rounded-lg hover:bg-[#5a085a] transition-colors text-xs font-medium"
                        >
                          <Plus className="w-3 h-3" />
                          1kg
                        </button>
                        <button
                          onClick={() => addFlakeItem('10kg')}
                          className="flex items-center gap-1 px-3 py-2 bg-[#7A0B7A] text-white rounded-lg hover:bg-[#5a085a] transition-colors text-xs font-medium"
                        >
                          <Plus className="w-3 h-3" />
                          10kg
                        </button>
                        <button
                          onClick={() => addFlakeItem('20kg')}
                          className="flex items-center gap-1 px-3 py-2 bg-[#7A0B7A] text-white rounded-lg hover:bg-[#5a085a] transition-colors text-xs font-medium"
                        >
                          <Plus className="w-3 h-3" />
                          20kg
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Note: 1kg and 10kg packs have limited colours (4 options). 20kg packs have all colours available (20 options).</p>
                    
                    {(manualOrder.flakeItems || []).length === 0 ? (
                      <div className="p-8 bg-gray-50 rounded-xl text-center">
                        <p className="text-gray-500">No flakes added yet. Click a size button above to add flakes.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(manualOrder.flakeItems || []).map((item, index) => {
                          const isLimited = item.packSize === '1kg' || item.packSize === '10kg';
                          const colorOptions = isLimited ? FLAKE_PRODUCTS.limitedColors : FLAKE_PRODUCTS.allColors;
                          const packIndex = item.packSize === '1kg' ? 0 : item.packSize === '10kg' ? 1 : 2;
                          const price = FLAKE_PRODUCTS.packs[packIndex].price;
                          
                          return (
                            <div key={item.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-100">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <span className="px-2 py-1 bg-[#7A0B7A]/10 text-[#7A0B7A] rounded text-xs font-bold">
                                    {item.packSize}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    £{price.toFixed(2)} each • {isLimited ? '4 colours' : 'All colours'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => removeFlakeItem(item.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                {/* Colour Selection */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Colour</label>
                                  <select
                                    value={item.color}
                                    onChange={(e) => updateFlakeItem(item.id, 'color', e.target.value)}
                                    className="w-full p-2 border border-gray-200 rounded-lg focus:border-[#7A0B7A] focus:ring-0 text-sm"
                                  >
                                    {colorOptions.map(color => (
                                      <option key={color.name} value={color.name}>{color.name}</option>
                                    ))}
                                  </select>
                                </div>
                                
                                {/* Quantity */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => updateFlakeItem(item.id, 'quantity', (item.quantity || 0) - 1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#7A0B7A] text-[#7A0B7A]"
                                    >
                                      <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="w-10 text-center font-bold">{item.quantity || 0}</span>
                                    <button
                                      onClick={() => updateFlakeItem(item.id, 'quantity', (item.quantity || 0) + 1)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Item subtotal */}
                              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                                <span className="text-gray-600">{item.quantity || 0} × {item.packSize} {item.color}</span>
                                <span className="font-bold text-[#7A0B7A]">
                                  £{((item.quantity || 0) * price).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Polyaspartic Section */}
                  <div>
                    <h3 className="font-bold text-[#27292E] mb-4 pb-2 border-b">Polyaspartic Seal Coat</h3>
                    
                    {/* 5m² */}
                    <div className="p-4 bg-gray-50 rounded-xl mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-[#27292E]">5m² Pack</h4>
                          <p className="text-xs text-gray-500">£{POLYASPARTIC_SEAL.packs[0].price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateManualOrder('polyaspartic5m2', manualOrder.polyaspartic5m2 - 1)} className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]">
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center font-bold text-lg">{manualOrder.polyaspartic5m2}</span>
                          <button onClick={() => updateManualOrder('polyaspartic5m2', manualOrder.polyaspartic5m2 + 1)} className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {manualOrder.polyaspartic5m2 > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                          <span className="text-gray-600">{manualOrder.polyaspartic5m2} × £{POLYASPARTIC_SEAL.packs[0].price.toFixed(2)}</span>
                          <span className="font-bold text-[#7A0B7A]">£{(manualOrder.polyaspartic5m2 * POLYASPARTIC_SEAL.packs[0].price).toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {/* 20m² */}
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-[#27292E]">20m² Pack</h4>
                          <p className="text-xs text-gray-500">£{POLYASPARTIC_SEAL.packs[1].price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateManualOrder('polyaspartic20m2', manualOrder.polyaspartic20m2 - 1)} className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]">
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center font-bold text-lg">{manualOrder.polyaspartic20m2}</span>
                          <button onClick={() => updateManualOrder('polyaspartic20m2', manualOrder.polyaspartic20m2 + 1)} className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {manualOrder.polyaspartic20m2 > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                          <span className="text-gray-600">{manualOrder.polyaspartic20m2} × £{POLYASPARTIC_SEAL.packs[1].price.toFixed(2)}</span>
                          <span className="font-bold text-[#7A0B7A]">£{(manualOrder.polyaspartic20m2 * POLYASPARTIC_SEAL.packs[1].price).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getManualOrderItemCount()} item{getManualOrderItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                      {voucherOff > 0 && <p className="text-xs text-amber-300 font-semibold mt-1" data-testid="pe-voucher-line">Training voucher − £{voucherOff.toFixed(2)} · to pay £{(calculateManualOrderTotal() - voucherOff).toFixed(2)}</p>}
                    </div>
                    <p className="text-3xl font-bold">£{calculateManualOrderTotal().toFixed(2)}</p>
                  </div>
                </div>

                {/* Priority */}
                <PrioritySelector value={priority} onChange={setPriority} />

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => setHighBuildOrderMode(null)}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => {
                      const items = buildManualItems(manualOrder);
                      if (items.length === 0) { toast.error('No products on this order yet.'); return; }
                      submitProEpoxyOrder({
                        system: 'high_build', mode: 'manual',
                        items, total: itemsTotal(items),
                        voucher_id: voucher.id || undefined, area_m2: voucherAreaM2,
                        priority,
                        selections: { manualOrder },
                      }, navigate);
                    }}
                    disabled={getManualOrderItemCount() === 0}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl disabled:opacity-50"
                    data-testid="proepoxy-complete-order-manual"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Complete Order
                  </Button>
                </div>
              </div>
  );
}
